import get from "lodash.get";

import ParkkiTheme from "themes/ParkkiTheme";
import Abilities from "services/permissions/abilities";
import CommonUtils from "services/common/utils";
import IntlUtils from "services/intl/utils";
import MapUtils from "services/map/utils";
import GlobalFiltersUtils from "services/globalFilters/utils";
import { FormattedMessage } from "react-intl";
import { DETAILS_API_TYPES } from "../common/enums";

class ParkStatisticsUtils {
  static getNormalAbsoluteValue = (current, previous) => {
    if (previous === 0) {
      return 0;
    }
    return Math.abs(Math.round(((current - previous) / previous) * 100));
  };

  static getWay = (current, previous) => {
    const compute = current - previous;
    switch (true) {
      case compute < 0:
        return -1;
      case compute > 0:
        return 1;
      default:
        return 0;
    }
  };

  static getColor = (current, previous) => {
    return current - previous < 0 ? "red" : "green";
  };

  static getWayByValue = (value) => {
    switch (true) {
      case value < 0:
        return -1;
      case value > 0:
        return 1;
      default:
        return 0;
    }
  };

  static getColorByValue = (value) => {
    return value < 0 ? "red" : "green";
  };

  static getAbsValue = (value) => {
    if (value === null || value === undefined) return null;

    return Math.abs(value);
  };

  static getAbsDiff = (a, b) => {
    return Math.abs(a - b);
  };

  static getRealTimeDailyAttendanceArray = (
    parkStatisticsState,
    attendanceLimitTime
  ) => {
    if (
      !parkStatisticsState ||
      !parkStatisticsState.dailyAttendance ||
      !parkStatisticsState.dailyAttendance.real_time
    )
      return [];
    return CommonUtils.formatGraphWithTimeLimit(
      parkStatisticsState.dailyAttendance.real_time,
      attendanceLimitTime
    );
  };

  static getPrevisionalDailyAttendanceArray = (
    parkStatisticsState,
    attendanceLimitTime
  ) => {
    if (
      !parkStatisticsState ||
      !parkStatisticsState.dailyAttendance ||
      !parkStatisticsState.dailyAttendance.previsional
    )
      return [];
    return CommonUtils.formatGraphWithTimeLimit(
      parkStatisticsState.dailyAttendance.previsional,
      attendanceLimitTime
    );
  };

  static formatAreasFromApi = (areas = [], config = []) => {
    const areasToDisplay = areas.filter((area) =>
      config.find(
        (areaConfig) => areaConfig?.area_id && areaConfig?.area_id === area?.id
      )
    );
    return areasToDisplay.map((area) =>
      ParkStatisticsUtils.formatAreaFromApi(area, config)
    );
  };

  static formatDetailsStatsFromApi = (data = {}) => {
    return {
      ...data,
      items: data?.areas?.map((item) => ({
        ...item,
        area_id: item.id,
        background: {
          config: ParkkiTheme.parkStatisticsAttendanceBar,
          value: item?.cars || 0,
          total: item?.capacity,
        },
      })),
    };
  };

  static formatAislesFromApi = (aisles = [], config = []) =>
    aisles.map((aisle) =>
      ParkStatisticsUtils.formatAisleFromApi(aisle, config)
    );

  static formatAreaFromApi = (area, config = []) => {
    const areaConfig = config.find(
      (config) =>
        get(area, "id", get(area, "area_id")) ===
        get(config, "area_id", get(config, "id"))
    );

    return {
      ...areaConfig,
      ...area,
      apiType: area?.type,
      type: "area",
      options: {
        ...get(area, "options", {}),
        ...get(areaConfig, "display", {}),
      },
    };
  };

  static formatAisleFromApi = (aisle, config = []) => {
    const aisleConfig = config.find(
      (config) =>
        (aisle?.id || aisle?.aisles_id) === (config?.aisle_id || config?.id)
    );

    return {
      ...aisleConfig,
      ...aisle,
      apiType: aisle?.type,
      type: "aisle",
      options: {
        ...(aisle?.options || {}),
        ...(aisleConfig?.display || {}),
      },
    };
  };

  static formatLayersInfosFromApi = (layersInfos, config) => {
    return {
      ...layersInfos,
      areas: ParkStatisticsUtils.formatAreasFromApi(
        layersInfos?.areas || [],
        config?.areas || []
      ),
      aisles: ParkStatisticsUtils.formatAislesFromApi(
        layersInfos?.aisles || [],
        config?.aisles || []
      ),
    };
  };

  static getDefaultSelectedLayers = (map) => {
    const selectedLayers = get(map, "areas", [])
      .map((area) => {
        if (get(area, "options.selected_by_default") === true) {
          return {
            ...area,
            type: "area",
          };
        }

        return undefined;
      })
      .filter((item) => item);

    return selectedLayers;
  };

  static getSelectedLayers = (parkStatisticsState) =>
    get(parkStatisticsState, "map.selectedLayers", []);

  static getGeneralStatsRow = () => {
    if (!Abilities.ability.can("read", "park_stats.general")) return null;
    const intl = IntlUtils.getIntlInstance();

    return [
      {
        toggle: Abilities.ability.can("read", "park_stats.general"),
        label: intl.messages["parkStatisticsScene.statsRow.nbSpotsLabel"],
        subLabel: intl.messages["parkStatisticsScene.statsRow.insideZone"],
        value: GlobalFiltersUtils.getParkStatsByKey("general", "nb_spots"),
        type: {
          name: "label",
          color: "blue",
        },
      },
      {
        toggle: Abilities.ability.can("read", "park_stats.general.carsParked"),
        label: (
          <FormattedMessage
            id="parkStatisticsScene.statsRow.parkedCarsLabel"
            values={{
              count: GlobalFiltersUtils.getParkStatsByKey(
                "general",
                "parked_cars"
              )?.value,
            }}
          />
        ),
        infos: intl.messages["parkStatisticsScene.statsRow.infos.parkedCars"],
        value: GlobalFiltersUtils.getParkStatsByKey("general", "parked_cars"),
        type: {
          toggle: Abilities.ability.can(
            "read",
            "park_stats.general.carsParked.avg"
          ),
          name: "normal",
          order: "first",
          retrieveNormal: ParkStatisticsUtils.getNormalAbsoluteValue,
          way: ParkStatisticsUtils.getWay,
          retrieveColor: ParkStatisticsUtils.getColor,
          symbol: "arrow",
          unity: "%",
        },
      },
      {
        toggle: Abilities.ability.can(
          "read",
          "park_stats.general.availableSpots"
        ),
        label: (
          <FormattedMessage
            id="parkStatisticsScene.statsRow.availableSpots"
            values={{
              count: GlobalFiltersUtils.getParkStatsByKey(
                "general",
                "available_spots"
              )?.value,
            }}
          />
        ),
        infos:
          intl.messages["parkStatisticsScene.statsRow.infos.availableSpots"],
        value: GlobalFiltersUtils.getParkStatsByKey(
          "general",
          "available_spots"
        ),
        type: {
          toggle: Abilities.ability.can(
            "read",
            "park_stats.general.availableSpots.avg"
          ),
          name: "normal",
          retrieveNormal: ParkStatisticsUtils.getNormalAbsoluteValue,
          way: ParkStatisticsUtils.getWay,
          retrieveColor: ParkStatisticsUtils.getColor,
          symbol: "arrow",
          unity: "%",
        },
      },
      {
        toggle: Abilities.ability.can(
          "read",
          "park_stats.general.carsParkedAverageDuration"
        ),
        label: intl.messages["parkStatisticsScene.statsRow.avgParkingDuration"],
        infos:
          intl.messages[
            "parkStatisticsScene.statsRow.infos.avgParkingDuration"
          ],
        value: {
          ...GlobalFiltersUtils.getParkStatsByKey(
            "general",
            "avg_parking_duration"
          ),
          getValue: (values) => values?.median,
          getAvg: (values) => values?.normal,
          formatValue: (val) => CommonUtils.humanizeMinutesToHours(val),
          formatNormal: (val) => CommonUtils.humanizeMinutesToHours(val),
        },
        type: {
          toggle: Abilities.ability.can(
            "read",
            "park_stats.general.carsParkedAverageDuration.avg"
          ),
          name: "normal",
          order: "last",
          retrieveNormal: ParkStatisticsUtils.getAbsDiff,
          way: ParkStatisticsUtils.getWay,
          retrieveColor: ParkStatisticsUtils.getColor,
          symbol: "arrow",
          unity: "",
          valueUnity: "",
        },
      },
    ];
  };

  static getKeyNumbersStatsRow = () => {
    if (!Abilities.ability.can("read", "park_stats.keys")) return null;
    const intl = IntlUtils.getIntlInstance();

    return [
      {
        toggle: Abilities.ability.can("read", "park_stats.keys"),
        label: intl.messages["parkStatisticsScene.statsRow.keyNumbersLabel"],
        subLabel: intl.messages["parkStatisticsScene.statsRow.insideZone"],
        type: {
          name: "label",
          color: "turquoise",
        },
      },
      {
        toggle: Abilities.ability.can("read", "park_stats.keys.pressure"),
        label: intl.messages["parkStatisticsScene.statsRow.parkingPressure"],
        value: GlobalFiltersUtils.getParkStatsByKey(
          "general",
          "parking_pressure"
        ),
        type: {
          name: "circular",
          order: "first-circular",
        },
      },
      {
        toggle: Abilities.ability.can("read", "park_stats.keys.flow"),
        label: intl.messages["parkStatisticsScene.statsRow.flow"],
        subLabel:
          intl.messages["parkStatisticsScene.statsRow.duringCurrentHour"],
        infos: intl.messages["parkStatisticsScene.statsRow.infos.flow"],
        value: GlobalFiltersUtils.getParkStatsByKey(
          "general",
          "hourly_variation"
        ),
        type: {
          name: "normalWithoutLabel",
          order: "last",
          way: ParkStatisticsUtils.getWayByValue,
          retrieveColor: ParkStatisticsUtils.getColorByValue,
          retrieveNormal: ParkStatisticsUtils.getAbsValue,
          symbol: "arrow",
          unity: " %",
        },
      },
    ];
  };

  static getControlsStatsRow = () => {
    if (!Abilities.ability.can("read", "park_stats.controls")) return null;
    const intl = IntlUtils.getIntlInstance();

    return [
      {
        toggle: Abilities.ability.can("read", "park_stats.controls"),
        label: intl.messages["parkStatisticsScene.statsRow.nbInfractionsLabel"],
        subLabel: intl.messages["parkStatisticsScene.statsRow.insideZone"],
        value: GlobalFiltersUtils.getParkStatsByKey(
          "controls",
          "total_infractions"
        ),
        type: {
          name: "label",
          color: "orange",
        },
      },
      {
        toggle: Abilities.ability.can(
          "read",
          "park_stats.controls.infractions"
        ),
        label: (
          <FormattedMessage
            id="parkStatisticsScene.statsRow.infractions"
            values={{
              count: GlobalFiltersUtils.getParkStatsByKey(
                "controls",
                "infractions"
              )?.value,
            }}
          />
        ),
        infos: intl.messages["parkStatisticsScene.statsRow.infos.infractions"],
        value: GlobalFiltersUtils.getParkStatsByKey("controls", "infractions"),
        type: {
          toggle: Abilities.ability.can(
            "read",
            "park_stats.controls.infractions.avg"
          ),
          name: "normal",
          order: "first",
          retrieveNormal: ParkStatisticsUtils.getNormalAbsoluteValue,
          way: ParkStatisticsUtils.getWay,
          retrieveColor: ParkStatisticsUtils.getColor,
          symbol: "sign",
        },
      },
      {
        toggle: Abilities.ability.can(
          "read",
          "park_stats.controls.obstructiveParking"
        ),
        label: (
          <FormattedMessage
            id="parkStatisticsScene.statsRow.obstructiveParking"
            values={{
              count: GlobalFiltersUtils.getParkStatsByKey(
                "controls",
                "obstructive_parking"
              )?.value,
            }}
          />
        ),
        infos:
          intl.messages[
            "parkStatisticsScene.statsRow.infos.obstructiveParking"
          ],
        value: GlobalFiltersUtils.getParkStatsByKey(
          "controls",
          "obstructive_parking"
        ),
        type: {
          toggle: Abilities.ability.can(
            "read",
            "park_stats.controls.obstructiveParking.avg"
          ),
          name: "normal",
          retrieveNormal: ParkStatisticsUtils.getNormalAbsoluteValue,
          way: ParkStatisticsUtils.getWay,
          retrieveColor: ParkStatisticsUtils.getColor,
          symbol: "sign",
        },
      },
      {
        toggle: Abilities.ability.can("read", "park_stats.controls.nonMoving"),
        label: (
          <FormattedMessage
            id="parkStatisticsScene.statsRow.nonMoving"
            values={{
              count: GlobalFiltersUtils.getParkStatsByKey(
                "controls",
                "non_moving"
              )?.value,
            }}
          />
        ),
        infos: intl.messages["parkStatisticsScene.statsRow.infos.nonMoving"],
        value: GlobalFiltersUtils.getParkStatsByKey("controls", "non_moving"),
        type: {
          toggle: Abilities.ability.can(
            "read",
            "park_stats.controls.nonMoving.avg"
          ),
          order: "last",
          name: "normal",
          retrieveNormal: ParkStatisticsUtils.getNormalAbsoluteValue,
          way: ParkStatisticsUtils.getWay,
          retrieveColor: ParkStatisticsUtils.getColor,
          symbol: "sign",
        },
      },
    ];
  };

  static getLineChartData = (state, type, accessor, size) => {
    return new Array(size).fill(get(state, `${type}.${accessor}`));
  };

  static getAttendanceLimitTime = (state) => {
    return (
      state?.dailyAttendance?.timeLimit ?? {
        left_time_limit: 0,
        right_time_limit: 24,
      }
    );
  };

  static getChartStats = (parkStatisticsState) => {
    const intl = IntlUtils.getIntlInstance();

    const attendanceLimitTime =
      ParkStatisticsUtils.getAttendanceLimitTime(parkStatisticsState);
    const attendanceRealTime =
      ParkStatisticsUtils.getRealTimeDailyAttendanceArray(
        parkStatisticsState,
        attendanceLimitTime
      );

    const attendancePrevisionalTime =
      ParkStatisticsUtils.getPrevisionalDailyAttendanceArray(
        parkStatisticsState,
        attendanceLimitTime
      );

    return {
      toggle: Abilities.ability.can("read", "park_stats.dailyAttendance"),
      labels: Object.keys(attendanceRealTime).map((v) => {
        return `${v}h`;
      }),
      datasets: [
        {
          type: "line",
          label: intl.messages["parkStatisticsScene.chart.capacity"],
          yLabel: "capacity",
          data: ParkStatisticsUtils.getLineChartData(
            parkStatisticsState,
            "dailyAttendance",
            "capacity",
            Object.values(attendanceRealTime).length
          ),
          fill: false,
          borderColor: ParkkiTheme.colors.grey,
          backgroundColor: "rgba(255, 255, 255, 0)",
          borderDash: [5, 5],
          pointHoverRadius: 0,
          pointHoverBorderWidth: 0,
          pointRadius: 0,
          borderWidth: 1,
        },
        {
          type: "line",
          label: intl.messages["parkStatisticsScene.chart.realTime"],
          data: Object.values(attendanceRealTime),
          fill: false,
          borderColor: ParkkiTheme.colors.red,
          backgroundColor: ParkkiTheme.colors.red,
          pointBorderColor: ParkkiTheme.colors.red,
          pointBackgroundColor: ParkkiTheme.colors.red,
          pointHoverBackgroundColor: ParkkiTheme.colors.red,
          pointHoverBorderColor: ParkkiTheme.colors.red,
        },
        {
          type: "bar",
          label: intl.messages["parkStatisticsScene.chart.previsional"],
          data: Object.values(attendancePrevisionalTime),
          backgroundColor: ParkStatisticsUtils.getBackgroundColorArray(
            get(
              Object.values(attendanceRealTime).filter(
                (item) => item !== null && item !== undefined
              ),
              "length",
              0
            ) - 1
          ),
          borderColor: ParkkiTheme.colors.turquoise,
          hoverBackgroundColor: ParkkiTheme.colors.turquoise,
          hoverBorderColor: ParkkiTheme.colors.turquoise,
        },
      ],
    };
  };

  static getAffluenceChartOptions = () => {
    const intl = IntlUtils.getIntlInstance();

    return {
      maintainAspectRatio: false,
      responsive: true,
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 12,
        },
      },
      tooltips: {
        enabled: true,
        mode: "index",
        callbacks: {
          title: (tooltipItems, data) =>
            `${intl.messages["parkStatisticsScene.chart.tooltipTitlePrefix"]} ${
              get(data, "labels", {})[tooltipItems[0].index]
            }`,
          label: (
            tooltipItem = { datasetIndex: null, index: null },
            data = { datasets: [] }
          ) => {
            const { datasetIndex, index } = tooltipItem;
            const { datasets } = data;
            const currentDataset = datasetIndex >= 0 && datasets[datasetIndex];
            const nbCars =
              (currentDataset &&
                currentDataset.data &&
                currentDataset.data[index]) ||
              intl.messages["parkStatisticsScene.chart.any"];

            return `${datasets[datasetIndex].label}: ${nbCars} ${
              intl.messages[
                `parkStatisticsScene.chart.car${nbCars > 1 ? "s" : ""}`
              ]
            }`;
          },
        },
      },
      scales: {
        xAxes: [
          {
            categoryPercentage: 1.0,
            barPercentage: 0.95,
            ticks: {
              autoSkip: true,
              maxTicksLimit: 10,
            },
            gridLines: {
              display: false,
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
            },
          },
        ],
      },
    };
  };

  static getAllLayers = (parkStatisticsState) => {
    return get(parkStatisticsState, "map.areas", []);
  };

  static getBackgroundColorArray = (realTimeLength) => {
    const defaultBarColor = ParkkiTheme.colors.grey;
    const otherColor = ParkkiTheme.colors.turquoise3;

    return Array(24)
      .fill()
      .map((v, i) => (i <= realTimeLength ? defaultBarColor : otherColor));
  };

  static getFilters = (type = "default", state, allAreas = false) => {
    const { radius, position } = state.GlobalFilters;
    const { areaType, timestamp } = state.ParkStatistics?.filters;
    const currentFloor = allAreas ? undefined : MapUtils.getFloor(state.Map);
    let data = {};
    let filters = {};

    if (get(areaType, "length", 0) > 0) {
      filters = {
        area: {
          "type ($IN)": areaType,
        },
      };
    }

    if (
      type === "radius" &&
      currentFloor !== null &&
      currentFloor !== undefined
    ) {
      filters = {
        ...filters,
        area: { ...get(filters, "area", {}), floor_level: currentFloor },
      };
    }

    const selectedLayers = allAreas
      ? ParkStatisticsUtils.getAllLayers(state.ParkStatistics)
      : ParkStatisticsUtils.getSelectedLayers(state.ParkStatistics);

    if (type === "areas") {
      const allowedDetailsApiTypes = [
        DETAILS_API_TYPES.PARK,
        DETAILS_API_TYPES.PARK_AND_FLOW,
      ];

      const areas = selectedLayers
        .filter(
          (layer) =>
            allAreas ||
            currentFloor === undefined ||
            currentFloor === null ||
            get(layer, "floor_level") === currentFloor
        )
        ?.filter((layer) => allowedDetailsApiTypes.indexOf(layer.apiType) >= 0)
        .map((layer) => get(layer, "id"));

      if (areas.length > 0) {
        data = { ...data, areas };
      }
    }

    if (type === "areasDetails") {
      const allowedDetailsApiTypes = [
        DETAILS_API_TYPES.PARK,
        DETAILS_API_TYPES.PARK_AND_FLOW,
      ];
      const areas = state.ParkStatistics?.map?.areas
        .filter(
          (layer) =>
            currentFloor === undefined ||
            currentFloor === null ||
            get(layer, "floor_level") === currentFloor
        )
        ?.filter((layer) => allowedDetailsApiTypes.indexOf(layer.apiType) >= 0)
        .map((layer) => get(layer, "id"));

      if (areas.length > 0) {
        data = { ...data, areas };
      }
    }

    if (type === "radius") {
      data = { ...data, radius, position };
    }

    return {
      ...data,
      filters,
      timestamp,
    };
  };

  static getSelectedLayersFromCurrentFloorLevel = (
    state,
    currentFloorLevel
  ) => {
    const globalSelectedLayers =
      ParkStatisticsUtils.getSelectedLayers(state.ParkStatistics) || [];

    return globalSelectedLayers
      .filter(
        (layer) =>
          currentFloorLevel === undefined ||
          currentFloorLevel === null ||
          get(layer, "floor_level") === currentFloorLevel
      )
      .map((selectedLayer) => get(selectedLayer, "id"));
  };

  static getAreasFromCurrentFloor = (state, currentFloorLevel) => {
    const currentFloor =
      currentFloorLevel !== null && currentFloorLevel !== undefined
        ? currentFloorLevel
        : MapUtils.getFloor(state.Map);
    const areas = state.ParkStatistics?.map?.areas || [];

    return areas
      .filter((area) => area?.type !== "UNEQUIPPED")
      .filter(
        (area) =>
          currentFloor === undefined ||
          currentFloor === null ||
          get(area, "floor_level") === currentFloor
      );
  };

  static formatAreasFromCurrentFloor = (state) => {
    const currentFloor = MapUtils.getFloor(state.Map);
    const selectedLayersFromCurrentFloor =
      this.getSelectedLayersFromCurrentFloorLevel(state, currentFloor);
    const currentFloorAreas = this.getAreasFromCurrentFloor(
      state,
      currentFloor
    );

    return currentFloorAreas?.map((area) => ({
      ...area,
      selected: selectedLayersFromCurrentFloor?.indexOf(area?.id) >= 0,
    }));
  };
}

export default ParkStatisticsUtils;
